import { useStrings } from "@1js/localization-components";
import {
  BookmarkToggleButton,
  Column,
  PromptCardSkeleton,
  PromptTabs,
  Row,
  type Filter,
} from "@1js/pl-card-components";
import type { Prompt } from "@1js/pl-types";
import { isLikedPrompt, isNonSharedUserPrompt } from "@1js/pl-utils";
import { Filter24Regular } from "@fluentui/react-icons";
import * as React from "react";
import usePageSize from "../../pages/prompts/allPromptsLibrary/AllPromptsLibraryPageSize.web";
import { LibraryContext } from "../../pages/prompts/allPromptsLibrary/LibraryContext";
import type { SearchResultsData } from "../../types";
import { useStyles } from "../AllPrompts/AllPrompts.web.styles";
import { EmptyStatePromptResults } from "../AllPrompts/EmptyStatePromptResults";
import { Filters } from "../AllPrompts/Filters.web";
import { PromptHeadingV2 } from "../AllPrompts/PromptHeadingV2";
import { useColumnStyle } from "../AllPrompts/useColumnStyle.hook";
import { PromptDetailModal } from "../PromptDetail/PromptDetailModal.web";
import type { WebPrompt } from "../data";
import type { PromptCardPanelProps } from "./PromptCardPanel";
import { PromptsSection } from "./PromptSection";
import { useYourPrompts } from "./YourPrompts.hook.web";
import { RecentlyLikedTitle, RecentlySavedTitle } from "./YourPrompts.string";

export type YourPromptsProps = PromptCardPanelProps & {
  isShowHasMore: boolean;
  hasMore?: boolean;
  isLoading: boolean;
  handleCardFocus: () => void;
  onMoreClicked: () => void;
  selectedFacets: Record<string, string[]>;
  filters: Filter[];
  handleFilterChange: (
    selectedValues: Record<string, string[]>,
    filters: Filter[]
  ) => void;
  skeletonCount: number;
  swipe: {
    setSwipedState: (swiped: boolean) => void;
  };
  dialogOpen: boolean;
  handleDialogClose: () => void;
  activePrompt: WebPrompt | undefined;
  morePrompts: WebPrompt[];
  onSharePromptClicked: ((prompt: Prompt) => void) | undefined;
  onCopyPromptClicked: ((prompt: Prompt) => void) | undefined;
  onTryInAppBadgeAction:
    | ((
        prompt: Prompt,
        product: string,
        tagRef: React.MutableRefObject<null>
      ) => void)
    | undefined;
  isShowFilters: boolean;
  children: React.ReactNode[] | React.ReactNode;
  onFiltersCleared: () => void;
  onFiltersOpenChanged: (isOpen: boolean, filters: Filter[]) => void;
  isMetaOsV2?: boolean;
  searchResultsData?: SearchResultsData;
  selectedTab: PromptTabs;
  isMetaOsV2HomePage?: boolean;
};

export const YourPrompts: React.FC<YourPromptsProps> = ({
  currentFocusedPromptId,
  setCurrentFocusedPromptId,
  isLoading,
  skeletonCount,
  promptsToDisplay,
  promptRefs,
  shareToWorkgroup,
  handleShareViaCopyLinkClicked,
  handleShareViaEmailClicked,
  handleCardClick,
  onPromptBookmarkToggled,
  onDelete,
  setLastFocusedPromptId,
  prompts,
  groups,
  isShowHasMore,
  hasMore,
  handleCardFocus,
  onMoreClicked,
  resultsAnnouncementId,
  isWorkgroupPublishingEnabled,
  dialogOpen,
  handleDialogClose,
  setDialogOpen,
  activePrompt,
  morePrompts,
  onSharePromptClicked,
  onCopyPromptClicked,
  onTryInAppBadgeAction,
  isShowFilters,
  onBookmarkToggled,
  filters,
  selectedFacets,
  handleFilterChange,
  children,
  handlePromptsTabSelected,
  onFiltersCleared,
  onFiltersOpenChanged,
  isMetaOsV2,
  searchResultsData,
  selectedTab,
  isMetaOsV2HomePage,
}: YourPromptsProps) => {
  const { state } = React.useContext(LibraryContext);
  const styles = useStyles();
  const { isFlightActive, handleOnBookmarkToggled, handleOnDelete } =
    useYourPrompts({
      handlePromptsTabSelected,
      onBookmarkToggled,
      onDelete,
      setDialogOpen,
    });
  const appearance = isFlightActive("isMosAppPromptCardV2Enabled")
    ? "web-v3"
    : "web";
  const cardColStyles = useColumnStyle();

  // extension filter is only available for suggested tab. Since we don't have separate states for each tab (yet), we
  // just filter it out on tabs that don't need it.
  filters =
    isFlightActive("isAgentPromptSavingEnabled") ||
    isFlightActive("isAgentPromptFavoriteEnabled")
      ? filters
      : filters.filter((filter) => filter.key !== "extension");

  // Keeping the "createdBy" filter only for metaOsV2 for Mos Ap V2 liked and saved prompts
  if (!isMetaOsV2) {
    filters = filters.filter((filter) => filter.key !== "createdBy");
  }

  const localizations = useStrings({
    RecentlyLikedTitle,
    RecentlySavedTitle,
  });

  const pageSize = usePageSize(PromptTabs.SavedPrompts, 8);

  // checking for any filter applied
  const selectedFacetsFlatArray = Object.entries(selectedFacets).flatMap(
    ([key, values]) => values.map((value) => ({ key, value }))
  );

  //Checking for no filter applied (selectedFacetsFlatArray.length <= 1 to handle the case where only one filter Created by is default selected)
  //Empty state will be shown if no prompts to display and no filter(1) applied
  if (
    isMetaOsV2 &&
    ((selectedTab === PromptTabs.SavedPrompts &&
      state.allFilteredPrompts.filter(isNonSharedUserPrompt).length === 0 &&
      selectedFacetsFlatArray.length <= 1) ||
      (selectedTab === PromptTabs.LikedPrompts &&
        state.allFilteredPrompts.filter(isLikedPrompt).length === 0 &&
        selectedFacetsFlatArray.length <= 1))
  ) {
    return (
      <>
        <PromptHeadingV2
          navItem={selectedTab}
          searchResultsData={searchResultsData}
          isMetaOsV2HomePage={isMetaOsV2HomePage}
        />
        <EmptyStatePromptResults navItem={selectedTab} />
      </>
    );
  }

  return (
    <>
      {isMetaOsV2 && (
        <PromptHeadingV2
          navItem={selectedTab}
          searchResultsData={searchResultsData}
          isMetaOsV2HomePage={isMetaOsV2HomePage}
        />
      )}
      {isShowFilters && (
        <div className={styles.filtersRow}>
          {!isMetaOsV2 &&
            isFlightActive("isBookmarksEnabled") &&
            !isFlightActive("isPromptSavingEnabled") && (
              <BookmarkToggleButton
                className={styles.bookmarkToggle}
                isLoading={isLoading}
                showBookmarkOnly={true}
                onClicked={handleOnBookmarkToggled}
              />
            )}
          <div className={styles.filtersContainer}>
            <div className={styles.filtersRow}>
              {isMetaOsV2 && <Filter24Regular className={styles.filterIcon} />}
              <Filters
                filters={filters}
                selectedFilters={selectedFacets}
                isLoading={isLoading}
                onFilterChanged={handleFilterChange}
                onCleared={onFiltersCleared}
                onOpenChanged={onFiltersOpenChanged}
                isMetaOsV2={isMetaOsV2}
              />
            </div>
          </div>
        </div>
      )}
      {children}
      {isLoading ? (
        <Row className={styles.cards} isMetaOsV2={isMetaOsV2}>
          {/* create skeletonCount number of PromptCardSkelton component */}
          {Array.from({ length: skeletonCount }).map((_, index) => (
            <Column key={index} size={3} className={cardColStyles}>
              <PromptCardSkeleton
                appearance={appearance}
                isFavoriteEnabled={isFlightActive("isBookmarksEnabled")}
              />
            </Column>
          ))}
        </Row>
      ) : (
        <>
          {(isMetaOsV2 && selectedTab === PromptTabs.SavedPrompts) ||
          !isMetaOsV2 ? (
            <PromptsSection
              seeAllFilter={{ createdBy: ["CreatedByMe"] }}
              collapsedViewPromptsCount={pageSize}
              headerTitle={localizations.RecentlySavedTitle()}
              prompts={prompts.filter(isNonSharedUserPrompt)}
              allFilteredPrompts={state.allFilteredPrompts.filter(
                isNonSharedUserPrompt
              )}
              resultsAnnouncementId={resultsAnnouncementId}
              promptsToDisplay={promptsToDisplay}
              setLastFocusedPromptId={setLastFocusedPromptId}
              setCurrentFocusedPromptId={setCurrentFocusedPromptId}
              promptRefs={promptRefs}
              currentFocusedPromptId={currentFocusedPromptId}
              isWorkgroupPublishingEnabled={isWorkgroupPublishingEnabled}
              shareToWorkgroup={shareToWorkgroup}
              handleShareViaCopyLinkClicked={handleShareViaCopyLinkClicked}
              handleShareViaEmailClicked={handleShareViaEmailClicked}
              handleCardClick={handleCardClick}
              onPromptBookmarkToggled={onPromptBookmarkToggled}
              onDelete={onDelete}
              groups={groups}
              handlePromptsTabSelected={handlePromptsTabSelected}
              onBookmarkToggled={onBookmarkToggled}
              setDialogOpen={setDialogOpen}
              isShowHasMore={isShowHasMore}
              hasMore={hasMore}
              isLoading={isLoading}
              handleCardFocus={handleCardFocus}
              onMoreClicked={onMoreClicked}
              filters={filters}
              selectedFacets={selectedFacets}
              handleFilterChange={handleFilterChange}
              isMetaOsV2={isMetaOsV2}
              selectedTab={selectedTab}
            />
          ) : null}
          {(isMetaOsV2 && selectedTab === PromptTabs.LikedPrompts) ||
          !isMetaOsV2 ? (
            <PromptsSection
              seeAllFilter={{ liked: ["Liked"] }}
              collapsedViewPromptsCount={pageSize}
              headerTitle={localizations.RecentlyLikedTitle()}
              prompts={prompts.filter(isLikedPrompt)}
              allFilteredPrompts={state.allFilteredPrompts.filter(
                isLikedPrompt
              )}
              resultsAnnouncementId={resultsAnnouncementId}
              promptsToDisplay={promptsToDisplay}
              setLastFocusedPromptId={setLastFocusedPromptId}
              setCurrentFocusedPromptId={setCurrentFocusedPromptId}
              promptRefs={promptRefs}
              currentFocusedPromptId={currentFocusedPromptId}
              isWorkgroupPublishingEnabled={isWorkgroupPublishingEnabled}
              shareToWorkgroup={shareToWorkgroup}
              handleShareViaCopyLinkClicked={handleShareViaCopyLinkClicked}
              handleShareViaEmailClicked={handleShareViaEmailClicked}
              handleCardClick={handleCardClick}
              onPromptBookmarkToggled={onPromptBookmarkToggled}
              onDelete={onDelete}
              groups={groups}
              handlePromptsTabSelected={handlePromptsTabSelected}
              onBookmarkToggled={onBookmarkToggled}
              setDialogOpen={setDialogOpen}
              isShowHasMore={isShowHasMore}
              hasMore={hasMore}
              isLoading={isLoading}
              handleCardFocus={handleCardFocus}
              onMoreClicked={onMoreClicked}
              filters={filters}
              selectedFacets={selectedFacets}
              handleFilterChange={handleFilterChange}
              isMetaOsV2={isMetaOsV2}
              selectedTab={selectedTab}
            />
          ) : null}
          {activePrompt ? (
            <PromptDetailModal
              dialogOpen={dialogOpen}
              setDialogOpen={setDialogOpen}
              handleDialogClose={handleDialogClose}
              prompt={activePrompt as WebPrompt}
              morePrompts={morePrompts}
              onMoreLikeThisClicked={handleCardClick}
              onBookmarkToggled={onPromptBookmarkToggled}
              onShareClicked={onSharePromptClicked}
              onCopyClicked={onCopyPromptClicked}
              isBookmarkEnabled={isFlightActive("isBookmarksEnabled")}
              isAgentPromptSavingEnabled={isFlightActive(
                "isAgentPromptSavingEnabled"
              )}
              isPromptSavingEnabled={isFlightActive("isPromptSavingEnabled")}
              isPromptSharingEnabled={isFlightActive("isPromptSharingEnabled")}
              isPromptSharingViaEmailEnabled={isFlightActive(
                "isPromptSharingViaEmailEnabled"
              )}
              isPromptSharingToWorkgroupEnabled={
                isWorkgroupPublishingEnabled &&
                (isFlightActive(
                  "isWorkgroupPublishingMicrosoftPromptsEnabled"
                ) ||
                  activePrompt.Origin === "User")
              }
              isPromptLikingEnabled={isFlightActive("isPromptLikingEnabled")}
              groups={groups}
              onShareToWorkgroupClicked={shareToWorkgroup}
              onDelete={handleOnDelete}
              isPromptCopyLinkEnabled={true}
              onTryInAppBadgeAction={onTryInAppBadgeAction || undefined}
            />
          ) : (
            <></>
          )}
        </>
      )}
    </>
  );
};
